import React, {useContext, useEffect, useState} from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';

import moment from 'moment';
import 'moment/locale/en-ca';
import 'moment/locale/fr-ca';

import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import withAppContext from "tbk-components/src/hooks/withAppContext";

export interface IIRStockChartProps extends IBaseElementProps {
	delayedPricingLabel?: string;
	todaysChangeLabel?: string;
	volumeLabel?: string;
	weekRangeLabel?: string;
	calianGroupLabel?: string;
	calianGroupStockSymbol?: string;
}

const IRStockChart: React.FC<IIRStockChartProps> = withAppContext(({
	id,
	className,
	classNames = [],
	delayedPricingLabel = 'Delayed pricing',
	todaysChangeLabel = "Today's change",
	volumeLabel = 'Volume',
	weekRangeLabel = '52 Wk range',
	calianGroupLabel = 'Calian Group Limited',
	calianGroupStockSymbol = 'TSX:CGY',
	context,
}) => {
	const { Api } = useContext( context );
	// Register required Chart.js elements
	ChartJS.register(
		CategoryScale,
		LinearScale,
		PointElement,
		LineElement,
		Title,
		Tooltip,
		Legend,
	);

	// Variables for linear gradient creation function
	let width;
	let height;
	let gradient;

	// Function to generate gradient for chart
	const getGradient = (ctx, chartArea) => {
		const chartWidth = chartArea.right - chartArea.left;
		const chartHeight = chartArea.bottom - chartArea.top;
		if (!gradient || width !== chartWidth || height !== chartHeight) {
			width = chartWidth;
			height = chartHeight;
			gradient = ctx.createLinearGradient(
				0,
				chartArea.bottom,
				0,
				chartArea.top,
			);
			gradient.addColorStop(0, '#007BC7');
			gradient.addColorStop(1, '#2308C9');
		}
		return gradient;
	};

	// Chart default options
	const chartOptions = {
		responsive: true,
		maintainAspectRatio: false,
		borderWidth: 4,
		plugins: {
			legend: {
				display: false,
			},
			title: {
				display: false,
			},
			tooltip: {
				backgroundColor: (context) => {
					const chart = context.chart;
					const { ctx, chartArea } = chart;

					if (!chartArea) {
						return;
					}
					return getGradient(ctx, chartArea);
				},
				cornerRadius: 16,
				caretSize: 8,
				padding: {
					top: 12,
					right: 16,
					bottom: 12,
					left: 16,
				},
				titleAlign: 'center',
				titleColor: '#FFFFFF',
				titleFont: {
					family: "'Plus Jakarta Sans', sans-serif",
					size: 12,
					weight: '400',
				},
				bodyAlign: 'center',
				bodyColor: '#FFFFFF',
				bodyFont: {
					family: "'Plus Jakarta Sans', sans-serif",
					size: 18,
					weight: '400',
				},
				displayColors: false,
			},
		},
		scales: {
			x: {
				ticks: {
					font: {
						family: "'Plus Jakarta Sans', sans-serif",
						size: 12,
						weight: '400',
					},
					color: '#2E2E2E',
					maxRotation: 46,
					minRotation: 46,
					padding: 8,
				},
				grid: {
					display: false,
					borderColor: 'transparent',
				},
			},
			y: {
				ticks: {
					font: {
						family: "'Plus Jakarta Sans', sans-serif",
						size: 12,
						weight: '400',
					},
					color: '#2E2E2E',
					padding: 8,
				},
				grid: {
					color: '#E9EBEC',
					borderColor: 'transparent',
				},
			},
		},
	};

	// Variables to store API Info
	// API KEY FOR LOCAL AND STAGING BELOW - REPLACE WITH PRODUCTION KEY WHEN AVAILABLE
	const advantageAPIKey = 'HCG2GO3EYSVAAK6H';

	// Set States
	const [chartLanguage, setChartLanguage] = useState('en');
	const [error, setError] = useState(null);
	const [isLoaded, setIsLoaded] = useState(false);
	const [quoteData, setQuoteData] = useState();
	const [dailyData, setDailyData] = useState();
	const [weeklyData, setWeeklyData] = useState();
	const [monthlyData, setMonthlyData] = useState();
	const [chartHeaderDataSources, setChartHeaderDataSources] = useState({
		delayedPricing: null,
		todaysChange: null,
		volume: null,
		wkRange: null,
	});
	const [chartDataSourceLabels, setChartDataSourceLabels] = useState([
		'3M',
		'6M',
		'1Y',
		'5Y',
		'Max',
	]);
	const [chartDataSources, setChartDataSources] = useState({
		threeMonthDataSource: null,
		sixMonthDataSource: null,
		twelveMonthDataSource: null,
		fiveYearDataSource: null,
		maxDataSource: null,
	});
	const [activeButton, setActiveButton] = useState(0);
	const [chartData, setChartData] = useState(null);

	// Use effect to fetch data from the API
	useEffect(() => {
		const currentPageLanguage = document.documentElement.lang;
		setChartLanguage(currentPageLanguage);
		const stockSymbol = 'TSX:CGY';
		Api.queryAlphaAdvantageStockChart({symbol:stockSymbol})
			.then(({globalQuoteResult,dailyDataResult,weeklyDataResult,monthlyDataResult}) => {
				if ( globalQuoteResult ) {
					setQuoteData( globalQuoteResult );
				}
				if ( dailyDataResult ) {
					setDailyData( dailyDataResult );
				}
				if ( weeklyDataResult ) {
					setWeeklyData( weeklyDataResult );
				}
				if ( monthlyDataResult ) {
					setMonthlyData( monthlyDataResult );
				}
			}).catch( e => {
				setError(e);
			});

	}, []);

	// Use effect to create chart data
	useEffect(() => {
		// Variables for chart data creation
		let delayedPricing = '';
		let todaysChange = '';
		let volume = '';
		let wkRange = '';
		let alltimeMonthlyDataArray = [];
		let oneMonthDataArray = [];
		let threeMonthDataArray = [];
		let sixMonthDataArray = [];
		let twelveMonthDataArray = [];
		let fiveYearDataArray = [];
		let maxDataArray = [];
		let threeMonthDataSource;
		let sixMonthDataSource;
		let twelveMonthDataSource;
		let fiveYearDataSource;
		let maxDataSource;

		// Function to create Chart Header Data
		const createChartHeaderData = () => {
			if (quoteData) {
				const quoteDataPrice = quoteData['05. price'];
				if (chartLanguage === 'fr-ca') {
					delayedPricing = new Intl.NumberFormat('fr-CA', {
						style: 'currency',
						currency: 'CAD',
					}).format(quoteDataPrice);
				} else {
					delayedPricing = new Intl.NumberFormat('en-US', {
						style: 'currency',
						currency: 'USD',
					}).format(quoteDataPrice);
				}

				todaysChange = Number(quoteData['09. change']).toFixed(2);

				let qouteDataChangeNumber = parseFloat(todaysChange);

				if (qouteDataChangeNumber > 0) {
					todaysChange = '+' + todaysChange;
				}

				volume = new Intl.NumberFormat('en-US').format(
					quoteData['06. volume'],
				);
			}

			if (weeklyData) {
				let weeklyDataArray = [];
				for (const [key, value] of Object.entries(weeklyData)) {
					weeklyDataArray.push({ key, value });
				}

				let recentWeeklyDataArray = weeklyDataArray.filter((item) => {
					let currentDate = moment();
					let itemDate = moment(item.key);
					let weekDifference = currentDate.diff(itemDate, 'weeks');

					if (weekDifference <= 52) {
						return item;
					}
				});

				let recentWeeklyDataArrayCloseValues =
					recentWeeklyDataArray.map((item) => {
						return item.value['4. close'];
					});

				let recentWeeklyDataArrayCloseValuesMin = Math.min(
					...recentWeeklyDataArrayCloseValues,
				);

				let recentWeeklyDataArrayCloseValuesMax = Math.max(
					...recentWeeklyDataArrayCloseValues,
				);

				if (chartLanguage === 'fr-ca') {
					wkRange =
						new Intl.NumberFormat('fr-CA', {
							style: 'currency',
							currency: 'CAD',
						}).format(recentWeeklyDataArrayCloseValuesMin) +
						' - ' +
						new Intl.NumberFormat('fr-CA', {
							style: 'currency',
							currency: 'CAD',
						}).format(recentWeeklyDataArrayCloseValuesMax);
				} else {
					wkRange =
						new Intl.NumberFormat('en-US', {
							style: 'currency',
							currency: 'USD',
						}).format(recentWeeklyDataArrayCloseValuesMin) +
						' - ' +
						new Intl.NumberFormat('en-US', {
							style: 'currency',
							currency: 'USD',
						}).format(recentWeeklyDataArrayCloseValuesMax);
				}
			}

			setChartHeaderDataSources({
				delayedPricing: delayedPricing,
				todaysChange: todaysChange,
				volume: volume,
				wkRange: wkRange,
			});
		};

		// Function to create / format all Chart Data
		const createAllChartData = () => {
			if (chartLanguage === 'fr-ca') {
				moment.locale('fr-ca');
			} else {
				moment.locale('en-ca');
			}

			if (
				quoteData !== null &&
				quoteData !== undefined &&
				monthlyData !== null &&
				monthlyData !== undefined
			) {
				for (const [key, value] of Object.entries(monthlyData)) {
					alltimeMonthlyDataArray.push({ key, value });

					if (
						alltimeMonthlyDataArray != null &&
						alltimeMonthlyDataArray != undefined
					) {
						if (alltimeMonthlyDataArray.length <= 12) {
							twelveMonthDataArray.push({
								key: moment(key).format('MMM'),
								value: value,
							});
						}
						if (alltimeMonthlyDataArray.length <= 6) {
							sixMonthDataArray.push({
								key: moment(key).format('MMM'),
								value: value,
							});
						}
						if (alltimeMonthlyDataArray.length <= 3) {
							threeMonthDataArray.push({
								key: moment(key).format('MMM'),
								value: value,
							});
						}
					}
				}

				if (alltimeMonthlyDataArray.length > 0) {
					fiveYearDataArray = alltimeMonthlyDataArray.filter(
						(item) =>
							moment(item.key).format('MM') === '01' &&
							moment(item.key).format('YYYY') >=
								moment().subtract(4, 'years').format('YYYY'),
					);

					fiveYearDataArray.forEach((item) => {
						item.key = moment(item.key).format('YYYY');
					});

					maxDataArray = alltimeMonthlyDataArray.filter(
						(item) => moment(item.key).format('MM') === '01',
					);

					maxDataArray.forEach((item) => {
						item.key = moment(item.key).format('YYYY');
					});
				}
			}
			if (dailyData !== null && dailyData !== undefined) {
				let dailyDataArray = [];

				for (const [key, value] of Object.entries(dailyData)) {
					dailyDataArray.push({ key, value });
				}

				oneMonthDataArray = dailyDataArray.slice(0, 30);

				oneMonthDataArray.forEach((item) => {
					item.key = moment(item.key).format('MMM DD');
				});
			}
		};

		// Function to create individual chart data sources
		const createChartDataSource = (dataSet) => {
			let dataSetXAxisLabels = [];
			let dataSetValues = [];

			dataSet.forEach((dataPoint) => {
				dataSetXAxisLabels.push(dataPoint.key);
				dataSetValues.push(dataPoint.value['4. close']);
			});

			dataSetXAxisLabels.reverse();
			dataSetValues.reverse();

			const dataSource = {
				labels: dataSetXAxisLabels,
				datasets: [
					{
						data: dataSetValues,
						lineTension: 0.3,
						borderColor: function (context) {
							const chart = context.chart;
							const { ctx, chartArea } = chart;

							if (!chartArea) {
								return;
							}
							return getGradient(ctx, chartArea);
						},
						pointBackgroundColor: '#0574B9',
						pointBorderColor: '#FFFAFA',
						pointBorderWidth: 4,
						pointRadius: 7,
						pointHoverBorderWidth: 4,
						pointHoverRadius: 7,
						pointHoverBackgroundColor: '#ffffff',
						pointHoverBorderColor: '#0574B9',
					},
				],
			};

			return dataSource;
		};

		// Function to create all chart data sources
		const createChartDataSources = () => {
			threeMonthDataSource = createChartDataSource(threeMonthDataArray);
			sixMonthDataSource = createChartDataSource(sixMonthDataArray);
			twelveMonthDataSource = createChartDataSource(twelveMonthDataArray);
			fiveYearDataSource = createChartDataSource(fiveYearDataArray);
			maxDataSource = createChartDataSource(maxDataArray);

			setChartDataSources({
				threeMonthDataSource: threeMonthDataSource,
				sixMonthDataSource: sixMonthDataSource,
				twelveMonthDataSource: twelveMonthDataSource,
				fiveYearDataSource: fiveYearDataSource,
				maxDataSource: maxDataSource,
			});
		};

		createChartHeaderData();
		createAllChartData();
		createChartDataSources();
	}, [quoteData, dailyData, weeklyData, monthlyData]);

	// Use effect to set chart data to one month data on initial render
	useEffect(() => {
		setChartData(chartDataSources.threeMonthDataSource);
		setIsLoaded(true);
	}, [chartHeaderDataSources, chartDataSources]);

	return (
		<div
			id={id}
			className={className || cn('calian-ir-stock-chart', ...classNames)}
		>
			<div className="calian-ir-stock-chart__container container-fluid">
				<div className="calian-ir-stock-chart__row row">
					<div className="calian-ir-stock-chart__column col">
						<div className="calian-ir-stock-chart__content">
							{!isLoaded && !error && (
								<div className="calian-ir-stock-chart__loading">
									<div
										className="spinner-border text-primary"
										role="status"
									>
										<span className="visually-hidden">
											Loading...
										</span>
									</div>
								</div>
							)}
							{(chartHeaderDataSources &&
								chartData &&
								isLoaded) && (
									<div className="calian-ir-stock-chart__content--inner">
										<div className="calian-ir-stock-chart__header">
											<div className="calian-ir-stock-chart__header--left">
												<div className="calian-ir-stock-chart__header--delayed-pricing">
													<p className="calian-ir-stock-chart__header--description-text">
														{delayedPricingLabel}
													</p>
													<p className="calian-ir-stock-chart__header--value">
														{chartHeaderDataSources.delayedPricing &&
															chartHeaderDataSources.delayedPricing}
													</p>
												</div>
											</div>
											<div className="calian-ir-stock-chart__header--right">
												<div className="calian-ir-stock-chart__header--todays-change">
													<p className="calian-ir-stock-chart__header--description-text">
														{todaysChangeLabel}
													</p>
													<p className="calian-ir-stock-chart__header--value">
														{chartHeaderDataSources.todaysChange &&
															chartHeaderDataSources.todaysChange}
													</p>
												</div>
												<div className="calian-ir-stock-chart__header--volume">
													<p className="calian-ir-stock-chart__header--description-text">
														{volumeLabel}
													</p>
													<p className="calian-ir-stock-chart__header--value">
														{chartHeaderDataSources.volume &&
															chartHeaderDataSources.volume}
													</p>
												</div>
												<div className="calian-ir-stock-chart__header--wk-range">
													<p className="calian-ir-stock-chart__header--description-text">
														{weekRangeLabel}
													</p>
													<p className="calian-ir-stock-chart__header--value">
														{chartHeaderDataSources.wkRange &&
															chartHeaderDataSources.wkRange}
													</p>
												</div>
											</div>
										</div>
										<div className="calian-ir-stock-chart__controls">
											<div className="calian-ir-stock-chart__controls--left">
												<p className="calian-ir-stock-chart__controls--title">
													{calianGroupLabel}
												</p>
												<p className="calian-ir-stock-chart__controls--code">
													{calianGroupStockSymbol}
												</p>
											</div>
											<div className="calian-ir-stock-chart__controls--right">
												{chartDataSources && (
													<ul className="nav nav-tabs">
														{Object.keys(
															chartDataSources,
														).map((key, index) => (
															<li
																className="nav-item"
																key={index}
															>
																<button
																	type="button"
																	className={`nav-link ${
																		activeButton ===
																		index
																			? 'active'
																			: ''
																	}`}
																	onClick={() => {
																		setChartData(
																			chartDataSources[
																				key
																			],
																		);
																		setActiveButton(
																			index,
																		);
																	}}
																>
																	{
																		chartDataSourceLabels[
																			index
																		]
																	}
																</button>
															</li>
														))}
													</ul>
												)}
											</div>
										</div>
										<div className="calian-ir-stock-chart__line-chart">
											<Line
												// TS Ignore is used to prevent unnecessary problems being logged because of React-Chartjs-2 Typescript definitions
												// @ts-ignore
												options={chartOptions}
												data={chartData}
											/>
										</div>
									</div>
								)}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
});

export default IRStockChart;
